// ============================================
// useVideoCall Hook
// Manages video call state and API interactions
// ============================================

import { useState, useCallback, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Hook for managing video calls
 */
const useVideoCall = (token) => {
  const [isInCall, setIsInCall] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [videoToken, setVideoToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  const pollIntervalRef = useRef(null);

  // Get auth headers
  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  // Start a 1:1 video call
  const startCall = useCallback(async (targetUserType, targetUserId) => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/video/call/start?target_user_type=${targetUserType}&target_user_id=${targetUserId}`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to start call';
        try {
          const data = await response.json();
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail) || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setCurrentRoom({
        id: data.room_id,
        hmsRoomId: data.hms_room_id,
        type: 'dm_call',
        invitationId: data.invitation_id
      });
      setVideoToken(data.token);
      setIsInCall(true);

      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, getHeaders]);

  // Join an existing room by getting a token
  const joinRoom = useCallback(async (roomId) => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/video/token?room_id=${roomId}`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to join room');
      }

      const data = await response.json();
      setCurrentRoom({
        id: data.room_id,
        hmsRoomId: data.hms_room_id,
        type: data.room_type
      });
      setVideoToken(data.token);
      setIsInCall(true);

      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, getHeaders]);

  // Accept an incoming call
  const acceptCall = useCallback(async (invitationId) => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/video/call/respond/${invitationId}?accept=true`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to accept call');
      }

      const data = await response.json();

      if (data.status === 'accepted') {
        setCurrentRoom({
          id: data.room_id,
          hmsRoomId: data.hms_room_id,
          type: 'dm_call'
        });
        setVideoToken(data.token);
        setIsInCall(true);

        // Remove from pending invitations
        setPendingInvitations(prev =>
          prev.filter(inv => inv.invitation_id !== invitationId)
        );
      }

      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, getHeaders]);

  // Decline an incoming call
  const declineCall = useCallback(async (invitationId) => {
    if (!token) return;

    try {
      await fetch(
        `${API_BASE}/api/video/call/respond/${invitationId}?accept=false`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );

      // Remove from pending invitations
      setPendingInvitations(prev =>
        prev.filter(inv => inv.invitation_id !== invitationId)
      );
    } catch (err) {
      console.error('Error declining call:', err);
    }
  }, [token, getHeaders]);

  // End the current call
  const endCall = useCallback(async () => {
    if (!token || !currentRoom) return;

    try {
      await fetch(
        `${API_BASE}/api/video/call/${currentRoom.id}/end`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );
    } catch (err) {
      console.error('Error ending call:', err);
    }

    setIsInCall(false);
    setCurrentRoom(null);
    setVideoToken(null);
  }, [token, currentRoom, getHeaders]);

  // Leave call (client-side only, doesn't end for others)
  const leaveCall = useCallback(() => {
    setIsInCall(false);
    setCurrentRoom(null);
    setVideoToken(null);
  }, []);

  // Start/join a channel huddle
  const joinHuddle = useCallback(async (channelId) => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/video/channels/${channelId}/huddle/start`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to join huddle');
      }

      const data = await response.json();
      setCurrentRoom({
        id: data.room_id,
        hmsRoomId: data.hms_room_id,
        type: 'channel_huddle',
        huddleId: data.huddle_id,
        channelId
      });
      setVideoToken(data.token);
      setIsInCall(true);

      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, getHeaders]);

  // Leave a channel huddle
  const leaveHuddle = useCallback(async (channelId) => {
    if (!token) return;

    try {
      await fetch(
        `${API_BASE}/api/video/channels/${channelId}/huddle/leave`,
        {
          method: 'POST',
          headers: getHeaders()
        }
      );
    } catch (err) {
      console.error('Error leaving huddle:', err);
    }

    setIsInCall(false);
    setCurrentRoom(null);
    setVideoToken(null);
  }, [token, getHeaders]);

  // Get huddle status for a channel
  const getHuddleStatus = useCallback(async (channelId) => {
    if (!token) return null;

    try {
      const response = await fetch(
        `${API_BASE}/api/video/channels/${channelId}/huddle`,
        {
          headers: getHeaders()
        }
      );

      if (!response.ok) return null;

      return await response.json();
    } catch (err) {
      console.error('Error getting huddle status:', err);
      return null;
    }
  }, [token, getHeaders]);

  // Poll for pending call invitations
  const fetchPendingInvitations = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/video/call/pending`,
        {
          headers: getHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingInvitations(data.invitations || []);
      }
    } catch (err) {
      console.error('Error fetching pending invitations:', err);
    }
  }, [token, getHeaders]);

  // Start polling for invitations when not in a call
  // Poll less frequently (30s) to avoid overwhelming database connections
  useEffect(() => {
    if (token && !isInCall) {
      // Delay initial fetch to avoid stampede on page load
      const initialDelay = setTimeout(() => {
        fetchPendingInvitations();
        pollIntervalRef.current = setInterval(fetchPendingInvitations, 30000);
      }, 5000);

      return () => {
        clearTimeout(initialDelay);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token, isInCall, fetchPendingInvitations]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return {
    // State
    isInCall,
    currentRoom,
    videoToken,
    isLoading,
    error,
    pendingInvitations,

    // Actions
    startCall,
    joinRoom,
    acceptCall,
    declineCall,
    endCall,
    leaveCall,
    joinHuddle,
    leaveHuddle,
    getHuddleStatus,

    // Clear error
    clearError: () => setError(null)
  };
};

export default useVideoCall;
